import { useEffect, useState, useRef } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { Search, Plus, Edit2, X, ChevronDown, Loader2, RefreshCw, ExternalLink, UserX, UserCheck } from 'lucide-react'
import { studentsApi, classesApi } from '../api/services.js'
import Avatar from '../components/Avatar.jsx'
import toast from 'react-hot-toast'

const emptyForm = {
  firstName: '', lastName: '', email: '', mobile: '',
  gender: 'Male', admissionNo: '', classId: '',
}

const getStudentName = (s) =>
  s.name || `${s.firstName || ''} ${s.lastName || ''}`.trim() || '—'

const getClassLabel = (s) =>
  s.classId?.name
    ? s.classId.name + (s.classId.section ? ' — ' + s.classId.section : '')
    : '—'

function SkeletonRow() {
  return (
    <tr className="animate-pulse">
      {[...Array(7)].map((_, i) => (
        <td key={i} className="table-td"><div className="h-3 bg-gray-100 rounded w-3/4" /></td>
      ))}
    </tr>
  )
}

export default function StudentsPage() {
  const location = useLocation()
  const navigate = useNavigate()
  const highlightId = location.state?.highlightId
  const highlightRef = useRef(null)

  const [students, setStudents] = useState([])
  const [classes, setClasses] = useState([])
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [search, setSearch] = useState('')
  const [filterClass, setFilterClass] = useState('all')
  const [modalOpen, setModalOpen] = useState(false)
  const [editing, setEditing] = useState(null)
  const [form, setForm] = useState(emptyForm)
  const [statusTarget, setStatusTarget] = useState(null) // { id, name, currentStatus }
  const [togglingStatus, setTogglingStatus] = useState(false)
  const [statusFilter, setStatusFilter] = useState('active')

  const fetchStudents = async () => {
    try {
      setLoading(true)
      const [studData, classData] = await Promise.all([
        studentsApi.getAll(),
        classesApi.getAll(),
      ])
      setStudents(Array.isArray(studData) ? studData : studData?.users ?? studData?.students ?? [])
      setClasses(Array.isArray(classData) ? classData : classData?.classes ?? [])
    } catch {
      toast.error('Failed to load students')
      setStudents([])
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => { fetchStudents() }, [])

  useEffect(() => {
    if (!highlightId || loading) return
    setTimeout(() => {
      if (highlightRef.current) {
        highlightRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' })
      }
    }, 100)
  }, [highlightId, loading])

  const filtered = students.filter((s) => {
    const name = getStudentName(s).toLowerCase()
    const admission = (s.admissionNo || '').toLowerCase()
    const q = search.toLowerCase()
    const matchSearch = name.includes(q) || admission.includes(q)
    const matchClass = filterClass === 'all' || (s.classId?._id || s.classId) === filterClass
    const matchStatus = statusFilter === 'all' || (s.accountStatus || 'active') === statusFilter
    return matchSearch && matchClass && matchStatus
  })

  const openAdd = () => {
    setEditing(null)
    setForm(emptyForm)
    setModalOpen(true)
  }

  const openEdit = (s) => {
    setEditing(s._id || s.id)
    setForm({
      firstName: s.firstName || '',
      lastName: s.lastName || '',
      email: s.email || '',
      mobile: s.mobile || '',
      gender: s.gender || 'Male',
      admissionNo: s.admissionNo || '',
      classId: s.classId?._id || s.classId || '',
    })
    setModalOpen(true)
  }

  const handleSave = async () => {
    if (!form.firstName.trim()) { toast.error('First name is required'); return }
    if (!form.admissionNo.trim()) { toast.error('Admission No is required'); return }
    try {
      setSaving(true)
      const payload = { ...form }
      if (editing) {
        const updated = await studentsApi.update(editing, payload)
        setStudents((prev) => prev.map((s) => (s._id || s.id) === editing ? { ...s, ...updated } : s))
        toast.success('Student updated')
      } else {
        const created = await studentsApi.create({ ...payload, role: 'student' })
        setStudents((prev) => [...prev, created])
        toast.success('Student added')
      }
      setModalOpen(false)
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to save student')
    } finally {
      setSaving(false)
    }
  }

  const handleToggleStatus = async () => {
    if (!statusTarget) return
    const nextStatus = statusTarget.currentStatus === 'inactive' ? 'active' : 'inactive'
    try {
      setTogglingStatus(true)
      const updated = await studentsApi.updateAccountStatus(statusTarget.id, nextStatus)
      setStudents((prev) => prev.map((s) =>
        (s._id || s.id) === statusTarget.id ? { ...s, ...updated, accountStatus: nextStatus } : s
      ))
      toast.success(`Student ${nextStatus === 'inactive' ? 'deactivated' : 'reactivated'}`)
      setStatusTarget(null)
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to update status')
    } finally {
      setTogglingStatus(false)
    }
  }

  return (
    <div className="space-y-5">
      <div className="flex flex-col sm:flex-row sm:items-center gap-3 justify-between">
        <div>
          <h2 className="text-xl font-bold text-gray-900">Students</h2>
          <p className="text-sm text-gray-500 mt-0.5">
            {loading ? '...' : `${students.filter(s => (s.accountStatus || 'active') === 'active').length} active · ${students.filter(s => s.accountStatus === 'inactive').length} inactive`}
          </p>
        </div>
        <div className="flex items-center gap-2 self-start sm:self-auto">
          <button onClick={fetchStudents} className="btn-secondary p-2" title="Refresh">
            <RefreshCw className="w-4 h-4" />
          </button>
          <button onClick={openAdd} className="btn-primary flex items-center gap-2">
            <Plus className="w-4 h-4" /> Add Student
          </button>
        </div>
      </div>

      <div className="card p-4 flex flex-col sm:flex-row gap-3">
        <div className="flex items-center gap-2 bg-gray-50 border border-gray-200 rounded-lg px-3 py-2 flex-1">
          <Search className="w-4 h-4 text-gray-400 flex-shrink-0" />
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search by name or admission no..."
            className="bg-transparent text-sm text-gray-700 placeholder-gray-400 outline-none w-full"
          />
        </div>
        <div className="relative">
          <select
            value={filterClass}
            onChange={(e) => setFilterClass(e.target.value)}
            className="input-field pr-8 appearance-none cursor-pointer"
          >
            <option value="all">All Classes</option>
            {classes.map((c) => (
              <option key={c._id || c.id} value={c._id || c.id}>
                {c.name}{c.section ? ` — ${c.section}` : ''}
              </option>
            ))}
          </select>
          <ChevronDown className="w-4 h-4 text-gray-400 absolute right-2.5 top-1/2 -translate-y-1/2 pointer-events-none" />
        </div>
        <div className="relative">
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="input-field pr-8 appearance-none cursor-pointer"
          >
            <option value="all">All Status</option>
            <option value="active">Active</option>
            <option value="inactive">Inactive</option>
          </select>
          <ChevronDown className="w-4 h-4 text-gray-400 absolute right-2.5 top-1/2 -translate-y-1/2 pointer-events-none" />
        </div>
      </div>

      <div className="card overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-gray-100">
                <th className="table-th">Name</th>
                <th className="table-th">Admission No</th>
                <th className="table-th">Class</th>
                <th className="table-th hidden md:table-cell">Gender</th>
                <th className="table-th hidden lg:table-cell">Email</th>
                <th className="table-th hidden lg:table-cell">Mobile</th>
                <th className="table-th">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-50">
              {loading ? (
                [...Array(6)].map((_, i) => <SkeletonRow key={i} />)
              ) : filtered.length === 0 ? (
                <tr>
                  <td colSpan={7} className="text-center py-10 text-gray-400 text-sm">
                    No students found
                  </td>
                </tr>
              ) : filtered.map((s) => {
                const sid = s._id || s.id
                const isHighlighted = highlightId && sid === highlightId
                const displayName = getStudentName(s)
                return (
                  <tr
                    key={sid}
                    ref={isHighlighted ? highlightRef : null}
                    className={`hover:bg-gray-50 transition-colors ${isHighlighted ? 'bg-blue-50 ring-2 ring-inset ring-blue-300' : ''} ${s.accountStatus === 'inactive' ? 'opacity-60' : ''}`}
                  >
                    <td className="table-td">
                      <div className="flex items-center gap-2.5">
                        <Avatar src={s.avatar} name={displayName} size="sm" />
                        <div>
                          <button
                            onClick={() => navigate(`/users/${sid}`)}
                            className="font-medium text-gray-900 hover:text-primary-700 transition-colors text-left"
                          >
                            {displayName}
                          </button>
                          {s.accountStatus === 'inactive' && (
                            <span className="block text-xs font-semibold text-red-500">Inactive</span>
                          )}
                        </div>
                      </div>
                    </td>
                    <td className="table-td text-gray-500">{s.admissionNo || '—'}</td>
                    <td className="table-td">
                      <span className="bg-blue-50 text-blue-700 text-xs font-semibold px-2 py-0.5 rounded-full">
                        {getClassLabel(s)}
                      </span>
                    </td>
                    <td className="table-td hidden md:table-cell text-gray-500">{s.gender || '—'}</td>
                    <td className="table-td hidden lg:table-cell text-gray-500">{s.email || '—'}</td>
                    <td className="table-td hidden lg:table-cell text-gray-500">{s.mobile || '—'}</td>
                    <td className="table-td">
                      <div className="flex items-center gap-1">
                        <button
                          onClick={() => navigate(`/users/${sid}`)}
                          className="p-1.5 rounded-lg text-gray-400 hover:text-primary-700 hover:bg-primary-50 transition-colors"
                          title="View profile"
                        >
                          <ExternalLink className="w-3.5 h-3.5" />
                        </button>
                        <button
                          onClick={() => openEdit(s)}
                          className="p-1.5 rounded-lg text-gray-400 hover:text-blue-600 hover:bg-blue-50 transition-colors"
                        >
                          <Edit2 className="w-3.5 h-3.5" />
                        </button>
                        <button
                          onClick={() => setStatusTarget({ id: sid, name: displayName, currentStatus: s.accountStatus || 'active' })}
                          className={`p-1.5 rounded-lg transition-colors ${s.accountStatus === 'inactive' ? 'text-gray-400 hover:text-green-600 hover:bg-green-50' : 'text-gray-400 hover:text-red-500 hover:bg-red-50'}`}
                          title={s.accountStatus === 'inactive' ? 'Reactivate student' : 'Deactivate student'}
                        >
                          {s.accountStatus === 'inactive'
                            ? <UserCheck className="w-3.5 h-3.5" />
                            : <UserX className="w-3.5 h-3.5" />
                          }
                        </button>
                      </div>
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>
      </div>

      {/* Add/Edit Modal */}
      {modalOpen && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100 sticky top-0 bg-white z-10">
              <h3 className="font-bold text-gray-900">{editing ? 'Edit Student' : 'Add Student'}</h3>
              <button onClick={() => setModalOpen(false)} className="text-gray-400 hover:text-gray-600">
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="p-6 grid grid-cols-2 gap-4">
              <div>
                <label className="block text-xs font-semibold text-gray-600 mb-1">First Name *</label>
                <input
                  className="input-field w-full"
                  value={form.firstName}
                  onChange={(e) => setForm({ ...form, firstName: e.target.value })}
                  placeholder="First name"
                />
              </div>
              <div>
                <label className="block text-xs font-semibold text-gray-600 mb-1">Last Name</label>
                <input
                  className="input-field w-full"
                  value={form.lastName}
                  onChange={(e) => setForm({ ...form, lastName: e.target.value })}
                  placeholder="Last name"
                />
              </div>
              <div>
                <label className="block text-xs font-semibold text-gray-600 mb-1">Admission No *</label>
                <input
                  className="input-field w-full"
                  value={form.admissionNo}
                  onChange={(e) => setForm({ ...form, admissionNo: e.target.value })}
                  placeholder="ADM-001"
                />
              </div>
              <div>
                <label className="block text-xs font-semibold text-gray-600 mb-1">Class</label>
                <select
                  className="input-field w-full"
                  value={form.classId}
                  onChange={(e) => setForm({ ...form, classId: e.target.value })}
                >
                  <option value="">— Select Class —</option>
                  {classes.map((c) => (
                    <option key={c._id || c.id} value={c._id || c.id}>
                      {c.name}{c.section ? ` — ${c.section}` : ''}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-xs font-semibold text-gray-600 mb-1">Gender</label>
                <select
                  className="input-field w-full"
                  value={form.gender}
                  onChange={(e) => setForm({ ...form, gender: e.target.value })}
                >
                  <option>Male</option>
                  <option>Female</option>
                </select>
              </div>
              <div>
                <label className="block text-xs font-semibold text-gray-600 mb-1">Mobile</label>
                <input
                  className="input-field w-full"
                  value={form.mobile}
                  onChange={(e) => setForm({ ...form, mobile: e.target.value })}
                  placeholder="10-digit number"
                />
              </div>
              <div className="col-span-2">
                <label className="block text-xs font-semibold text-gray-600 mb-1">Email</label>
                <input
                  className="input-field w-full"
                  value={form.email}
                  onChange={(e) => setForm({ ...form, email: e.target.value })}
                  placeholder="email@example.com"
                />
              </div>
            </div>
            <div className="px-6 pb-5 flex gap-3 justify-end">
              <button onClick={() => setModalOpen(false)} className="btn-secondary" disabled={saving}>
                Cancel
              </button>
              <button onClick={handleSave} disabled={saving} className="btn-primary flex items-center gap-2">
                {saving && <Loader2 className="w-4 h-4 animate-spin" />}
                {editing ? 'Update' : 'Add Student'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Activate / Deactivate Confirm */}
      {statusTarget && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-sm p-6 text-center">
            <div className={`w-12 h-12 rounded-full flex items-center justify-center mx-auto mb-4 ${statusTarget.currentStatus === 'inactive' ? 'bg-green-50' : 'bg-red-50'}`}>
              {statusTarget.currentStatus === 'inactive'
                ? <UserCheck className="w-5 h-5 text-green-500" />
                : <UserX className="w-5 h-5 text-red-500" />
              }
            </div>
            <h3 className="font-bold text-gray-900 mb-1">
              {statusTarget.currentStatus === 'inactive' ? 'Reactivate' : 'Deactivate'} Student?
            </h3>
            <p className="text-sm text-gray-500 mb-5">
              {statusTarget.currentStatus === 'inactive'
                ? `${statusTarget.name} will be able to log in again.`
                : `${statusTarget.name} will lose access but data will be preserved.`
              }
            </p>
            <div className="flex gap-3">
              <button onClick={() => setStatusTarget(null)} className="btn-secondary flex-1" disabled={togglingStatus}>
                Cancel
              </button>
              <button
                onClick={handleToggleStatus}
                disabled={togglingStatus}
                className={`flex-1 text-white text-sm font-semibold px-4 py-2 rounded-lg transition-colors flex items-center justify-center gap-2 ${statusTarget.currentStatus === 'inactive' ? 'bg-green-500 hover:bg-green-600' : 'bg-red-500 hover:bg-red-600'}`}
              >
                {togglingStatus && <Loader2 className="w-4 h-4 animate-spin" />}
                {statusTarget.currentStatus === 'inactive' ? 'Reactivate' : 'Deactivate'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
